import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { useAppStore } from '@restorant/core';
import type { AppSettings, RestaurantTable } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT, useTableLabel } from '../i18n';
import { useRestaurantTables, useSettings } from '../menu';
import { Icon } from '../icons/Icon';
import { EmptyNote } from '../components/EmptyNote';
import { SectionTitle } from '../components/SectionTitle';
import { T } from '../components/Text';
import { TableEditor } from '../components/TableEditor';
import { TextField } from '../components/TextField';
import { MiniButton } from '../components/MiniButton';

function TableRow({ table, onEdit }: { table: RestaurantTable; onEdit: () => void }) {
  const colors = useColors();
  const t = useT();
  const label = useTableLabel();
  // A table with an order in progress must not be quietly retired underneath
  // the person serving it, so the row says so.
  const busy = useAppStore((s) => (s.tables[table.id]?.cart.length ?? 0) > 0);

  const detail = [
    table.kind === 'parcel' ? t('kind_parcel') : t('kind_dine_in'),
    table.seats > 0 ? `${table.seats} ${t('seats')}` : null,
    table.active ? null : t('hidden_label'),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Pressable
      onPress={onEdit}
      style={({ hovered }: any) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: hovered ? colors.goldDim : colors.line,
        backgroundColor: colors.surface,
        marginBottom: 8,
        opacity: table.active ? 1 : 0.55,
      })}
    >
      <Icon
        name={table.kind === 'parcel' ? 'parcel' : 'table'}
        size={18}
        color={busy ? colors.gold : colors.textFaint}
      />
      <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
        <T size={13.5} weight={700} numberOfLines={1}>
          {label(table.id)}
        </T>
        <T size={11.5} color={colors.textFaint} numberOfLines={1}>
          {detail}
        </T>
      </View>
      {busy ? <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.good }} /> : null}
      <Icon name="edit" size={15} color={colors.textFaint} />
    </Pressable>
  );
}

/** Restaurant name, contact details and the discount rules. */
function RestaurantSettings() {
  const colors = useColors();
  const t = useT();
  const settings = useSettings();
  const updateSettings = useAppStore((s) => s.updateSettings);
  const showToast = useAppStore((s) => s.showToast);

  // Edited locally and saved on the button, not on every keystroke: each save
  // is one queued write, and a half-typed GST number should not reach the
  // other tills.
  const [draft, setDraft] = useState<AppSettings>(settings);
  const [presets, setPresets] = useState(settings.discountPresets.join(', '));
  const [dirty, setDirty] = useState(false);

  const patch = (p: Partial<AppSettings>) => {
    setDraft((d) => ({ ...d, ...p }));
    setDirty(true);
  };

  const save = () => {
    const list = presets
      .split(',')
      .map((v) => parseInt(v.trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0 && n <= 100);

    updateSettings({
      ...draft,
      // An empty list would leave no quick-discount buttons at all, so the
      // previous set is kept rather than wiping them.
      discountPresets: list.length ? list : settings.discountPresets,
    });
    setDirty(false);
    showToast(t('saved_toast'));
  };

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radius.lg,
        backgroundColor: colors.surface,
        padding: 16,
        gap: 14,
        marginBottom: 20,
      }}
    >
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <TextField
          testID="set-brand-name"
          label={t('brand_name')}
          value={draft.brandName}
          onChangeText={(v) => patch({ brandName: v })}
        />
        <TextField
          testID="set-brand-tagline"
          label={t('brand_tagline')}
          value={draft.brandTagline}
          onChangeText={(v) => patch({ brandTagline: v })}
        />
      </View>

      <TextField
        testID="set-address"
        label={t('address')}
        value={draft.address}
        onChangeText={(v) => patch({ address: v })}
      />

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <TextField
          testID="set-phone"
          label={t('customer_phone')}
          icon="phone"
          keyboardType="phone-pad"
          value={draft.phone}
          onChangeText={(v) => patch({ phone: v })}
        />
        <TextField
          testID="set-gstin"
          label={t('gstin')}
          value={draft.gstin}
          onChangeText={(v) => patch({ gstin: v.toUpperCase() })}
        />
      </View>

      <View style={{ gap: 6 }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
          <TextField
            testID="set-presets"
            label={t('discount_presets')}
            value={presets}
            onChangeText={(v) => {
              setPresets(v.replace(/[^0-9,\s]/g, ''));
              setDirty(true);
            }}
            placeholder="10, 20"
          />
          <TextField
            testID="set-max-discount"
            label={t('max_discount')}
            value={String(draft.maxDiscountPct)}
            keyboardType="number-pad"
            onChangeText={(v) =>
              patch({ maxDiscountPct: Math.min(100, parseInt(v.replace(/[^0-9]/g, ''), 10) || 0) })
            }
          />
        </View>
        <T size={11.5} color={colors.textFaint}>
          {t('discount_presets_hint')}
        </T>
      </View>

      {dirty ? <MiniButton label={t('save')} active onPress={save} /> : null}
    </View>
  );
}

/** Menu tab → Tables. The floor plan and the restaurant's own details. */
export function ManageTablesView() {
  const colors = useColors();
  const t = useT();
  const tables = useRestaurantTables(true);
  const [editor, setEditor] = useState<{ open: boolean; table: RestaurantTable | null }>({
    open: false,
    table: null,
  });

  return (
    <View>
      <SectionTitle title={t('restaurant')} icon="store" first />
      <RestaurantSettings />

      <SectionTitle title={t('tables_tab')} icon="table" />
      {tables.length ? (
        tables.map((r) => (
          <TableRow key={r.id} table={r} onEdit={() => setEditor({ open: true, table: r })} />
        ))
      ) : (
        <EmptyNote>{t('no_tables_yet')}</EmptyNote>
      )}

      <Pressable
        onPress={() => setEditor({ open: true, table: null })}
        testID="add-floor-table"
        style={({ hovered }: any) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 9,
          marginTop: 4,
          paddingVertical: 15,
          borderRadius: radius.lg,
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: hovered ? colors.gold : colors.line,
        })}
      >
        <Icon name="plus" size={17} color={colors.textDim} />
        <T size={13.5} weight={700} color={colors.textDim}>
          {t('add_floor_table')}
        </T>
      </Pressable>

      <TableEditor
        visible={editor.open}
        table={editor.table}
        onClose={() => setEditor({ open: false, table: null })}
      />
    </View>
  );
}
